import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, Building2, FileText, Handshake } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { useToast } from '../../../../components/ui/use-toast';
import ProductAutocomplete from '../../../../components/shared/ProductAutocomplete';
import InternalDataWarning from '../../../../components/shared/InternalDataWarning';
import PaymentTermPicker from '../../../../components/shared/PaymentTermPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback defaults — used if the CMS-managed template cannot be loaded.
// The CMS templates live at /admin/cms/document-templates (codes:
// `loi_default`, etc.) and take priority over these static strings.
const DEFAULT_RFQ_NOTES = '';
const DEFAULT_RFQ_TERMS = 'Quotation must remain valid for 30 days. Prices must be in the stated currency and include all applicable duties. Lead time and payment terms must be clearly stated.';
const DEFAULT_LOI_TERMS = 'This Letter of Intent is non-binding and pre-contractual. Full enforceability is subject to the execution of a Purchase Order. All commercial conditions remain to be negotiated in good faith.';
const DEFAULT_LOI_BODY_FALLBACK = (
  'Letter of Intent to Initiate Supply Process\n\n'
  + 'Dear [name of institution],\n\n'
  + 'Grupo BMP hereby formally expresses its intent to initiate a supply process under the terms and conditions set out in this document.\n\n'
  + '- This document is pre-contractual and non-binding; its full enforceability is subject to the execution of the final Purchase Order.\n'
  + '- The validity of this Letter of Intent is 30 days from the date of issuance.\n'
  + '- The reference currency is AED unless otherwise expressly agreed.\n'
  + '- Any formal proposal must be submitted on or before the stated due date.\n\n'
  + 'We await your acknowledgement and remain available for any clarification through our procurement office.\n\n'
  + 'Best regards,\nGrupo BMP'
);

const BLANK_ITEM = {
  product_id: null, product_name: '', sku: '', description: '',
  quantity: 1, unit: 'units', unit_price: null, tax_rate: 5, total: null,
};

const CreateRFQModal = ({ isOpen, onClose, onSuccess, editingRfq = null }) => {
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [companySnapshot, setCompanySnapshot] = useState(null);
  const [loiTemplateBody, setLoiTemplateBody] = useState(null); // cached from CMS
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const isEditMode = !!editingRfq;

  const [formData, setFormData] = useState({
    document_type: 'rfq',
    company_id: '',
    supplier_id: '',
    project_name: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    currency: 'AED',
    notes: DEFAULT_RFQ_NOTES,
    terms: DEFAULT_RFQ_TERMS,
    items: [{ ...BLANK_ITEM }],
  });

  // Seed form from editingRfq every time the modal opens in edit mode
  useEffect(() => {
    if (!isOpen || !editingRfq) return;
    setFormData({
      document_type: editingRfq.document_type || 'rfq',
      company_id: editingRfq.company_id || '',
      supplier_id: editingRfq.supplier_id || '',
      project_name: editingRfq.project_name || '',
      issue_date: editingRfq.issue_date || new Date().toISOString().split('T')[0],
      due_date: editingRfq.due_date || new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0],
      currency: editingRfq.currency || 'AED',
      notes: editingRfq.notes || '',
      terms: editingRfq.terms || '',
      items: (editingRfq.items && editingRfq.items.length > 0
        ? editingRfq.items.map((it) => ({
            product_id: it.product_id || null,
            product_name: it.product_name || '',
            sku: it.sku || '',
            description: it.description || '',
            quantity: it.quantity ?? 1,
            unit: it.unit || 'units',
            unit_price: it.unit_price ?? null,
            tax_rate: it.tax_rate ?? 5,
            total: it.total ?? null,
          }))
        : [{ ...BLANK_ITEM }]),
    });
    if (editingRfq.company_id) loadCompanySnapshot(editingRfq.company_id);
  }, [isOpen, editingRfq]); // eslint-disable-line

  // Inline "New supplier" mini-form
  const [showNewSupplier, setShowNewSupplier] = useState(false);
  const [creatingSupplier, setCreatingSupplier] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ company_name: '', email: '', phone: '', tax_id: '' });

  const isLoi = formData.document_type === 'loi';
  const defaultTermsRfq = DEFAULT_RFQ_TERMS;
  const defaultTermsLoi = DEFAULT_LOI_TERMS;

  useEffect(() => {
    if (isOpen) { fetchCompanies(); fetchSuppliers(); fetchLoiTemplate(); }
  }, [isOpen]);

  // Fetch the CMS-managed LOI default template once per mount — falls back
  // silently if the endpoint is not reachable or the template is missing.
  const fetchLoiTemplate = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/cms/document-templates/by-code/loi_default`);
      const body = r.data?.body_en || r.data?.body_pt || null;
      if (body) setLoiTemplateBody(body);
    } catch (_) { /* silent fallback */ }
  };

  // Swap defaults when toggling RFQ <-> LOI
  //   - terms: swap only if still on a known default (so user edits are preserved)
  //   - notes: the LOI uses a rich template; RFQ uses empty by default
  //   - In EDIT mode we keep whatever the user had in the DB — no auto-swap.
  useEffect(() => {
    if (isEditMode) return;
    setFormData((p) => {
      let terms = p.terms;
      if (p.terms === defaultTermsRfq || p.terms === defaultTermsLoi || !p.terms) {
        terms = isLoi ? defaultTermsLoi : defaultTermsRfq;
      }
      let notes = p.notes;
      const loiBodyInUse = loiTemplateBody || DEFAULT_LOI_BODY_FALLBACK;
      const onLoiDefault = (notes || '').trim().startsWith('Letter of Intent');
      if (isLoi && (!notes || notes === DEFAULT_RFQ_NOTES)) {
        notes = loiBodyInUse;
      } else if (!isLoi && onLoiDefault) {
        notes = DEFAULT_RFQ_NOTES;
      }
      return { ...p, terms, notes };
    });
  }, [isLoi, loiTemplateBody, isEditMode]); // eslint-disable-line

  const fetchCompanies = async () => {
    try {
      const res = await fetch(`${API_URL}/api/procurement/companies`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setCompanies(data);
      if (data.length === 1) {
        setFormData(prev => ({ ...prev, company_id: data[0].id }));
        loadCompanySnapshot(data[0].id);
      }
    } catch (e) { console.error(e); }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/procurement/suppliers`, { headers: { 'Authorization': `Bearer ${token}` } });
      setSuppliers(await res.json());
    } catch (e) { console.error(e); }
  };

  const loadCompanySnapshot = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/procurement/companies/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const company = await res.json();
      setCompanySnapshot(company);
      setFormData(prev => ({ ...prev, currency: company.currency || 'AED' }));
    } catch (e) { console.error(e); }
  };

  const handleItemChange = (index, field, value) => {
    const items = [...formData.items];
    items[index] = { ...items[index], [field]: ['product_name', 'sku', 'description', 'unit'].includes(field) ? value : (value === '' ? null : parseFloat(value) || 0) };
    setFormData({ ...formData, items });
  };

  // Called when the autocomplete selects (or creates) a product — pre-fills everything
  const pickProduct = (index, product) => {
    const items = [...formData.items];
    items[index] = {
      ...items[index],
      product_id: product.id || null,
      product_name: product.name || '',
      sku: product.sku || '',
      description: product.description || items[index].description || '',
      unit_price: product.price != null ? Number(product.price) : items[index].unit_price,
      tax_rate: product.tax_rate != null ? Number(product.tax_rate) : items[index].tax_rate,
    };
    setFormData({ ...formData, items });
  };

  const addItem = () => {
    setFormData({ ...formData, items: [...formData.items, { ...BLANK_ITEM }] });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };

  // Save a brand-new supplier on the fly, pre-select it in the form
  const saveNewSupplier = async () => {
    if (!newSupplier.company_name?.trim()) return;
    setCreatingSupplier(true);
    try {
      const r = await axios.post(`${API_URL}/api/procurement/suppliers`, {
        company_name: newSupplier.company_name.trim(),
        email: newSupplier.email || null,
        phone: newSupplier.phone || null,
        tax_id: newSupplier.tax_id || null,
        status: 'active',
      }, { headers: { Authorization: `Bearer ${token}` } });
      const created = r.data;
      setSuppliers((p) => [created, ...p]);
      setFormData((p) => ({ ...p, supplier_id: created.id }));
      setShowNewSupplier(false);
      setNewSupplier({ company_name: '', email: '', phone: '', tax_id: '' });
      toast({ title: 'Supplier created', description: created.company_name });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not create supplier',
        description: err.response?.data?.detail || 'Error',
      });
    } finally {
      setCreatingSupplier(false);
    }
  };

  // When supplier / company / dates change on LOI, auto-replace placeholders
  // used by the CMS-managed LOI template body.
  useEffect(() => {
    if (!isLoi || !formData.notes) return;
    const sup = suppliers.find((s) => s.id === formData.supplier_id);
    const comp = companies.find((c) => c.id === formData.company_id);
    const supName = sup?.company_name || sup?.name;
    const compName = comp?.trading_name || comp?.legal_name;

    const subs = {
      '[name of institution]': supName, // legacy token from previous versions
      '{supplier_name}': supName,
      '{company_name}': compName,
      '{project_name}': formData.project_name,
      '{issue_date}': formData.issue_date,
      '{due_date}': formData.due_date,
      '{currency}': formData.currency,
      '{validity_days}': '30',
    };

    setFormData((p) => {
      let next = p.notes;
      let touched = false;
      for (const [token, value] of Object.entries(subs)) {
        if (!value) continue;
        if (next.includes(token)) {
          next = next.split(token).join(value);
          touched = true;
        }
      }
      return touched ? { ...p, notes: next } : p;
    });
  }, [formData.supplier_id, formData.company_id, formData.project_name, formData.issue_date, formData.due_date, formData.currency, isLoi, suppliers, companies]); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_id || !formData.supplier_id) {
      toast({ variant: 'destructive', title: 'Error', description: 'Please select company and supplier' });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        document_type: formData.document_type,
        company_id: formData.company_id,
        supplier_id: formData.supplier_id,
        project_name: formData.project_name || null,
        issue_date: formData.issue_date,
        due_date: formData.due_date,
        currency: formData.currency,
        notes: formData.notes,
        terms: formData.terms,
        items: formData.items.map(item => ({
          product_id: item.product_id || null,
          product_name: item.product_name,
          sku: item.sku || null,
          description: item.description,
          quantity: item.quantity || 1,
          unit: item.unit || 'units',
          unit_price: item.unit_price,
          tax_rate: item.tax_rate || 0,
          total: item.total
        }))
      };
      const url = isEditMode
        ? `${API_URL}/api/procurement/rfqs/${editingRfq.id}`
        : `${API_URL}/api/procurement/rfqs`;
      const method = isEditMode ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      const data = await res.json();
      toast({
        title: 'Success',
        description: isEditMode
          ? `${(formData.document_type === 'loi' ? 'LOI' : 'RFQ')} ${data.rfq_number || editingRfq.rfq_number} updated`
          : `${(formData.document_type === 'loi' ? 'LOI' : 'RFQ')} ${data.rfq_number} created`,
      });
      onSuccess(data);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-xl font-bold text-[#2B2D42]">
            {isEditMode ? 'Edit' : 'Create'} {isLoi ? 'Letter of Intent (LOI)' : 'RFQ'}
          </h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Document type toggle — RFQ vs LOI */}
          <div className="grid grid-cols-2 gap-3" data-testid="rfq-document-type-toggle">
            <button
              type="button"
              onClick={() => setFormData((p) => ({ ...p, document_type: 'rfq' }))}
              className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 text-sm font-semibold transition-all ${
                !isLoi
                  ? 'border-[#FF6B35] bg-[#FF6B35]/10 text-[#E5562D]'
                  : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
              }`}
              data-testid="rfq-document-type-rfq"
            >
              <FileText className="w-4 h-4" /> Request for Quotation
            </button>
            <button
              type="button"
              onClick={() => setFormData((p) => ({ ...p, document_type: 'loi' }))}
              className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 text-sm font-semibold transition-all ${
                isLoi
                  ? 'border-[#023E8A] bg-[#023E8A]/10 text-[#023E8A]'
                  : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
              }`}
              data-testid="rfq-document-type-loi"
            >
              <Handshake className="w-4 h-4" /> Letter of Intent (LOI)
            </button>
          </div>
          <p className="text-xs text-gray-500 -mt-3">
            {isLoi
              ? 'LOI formalises pre-contractual intent before a binding Purchase Order.'
              : 'RFQ requests pricing, lead time and terms from the selected supplier.'}
          </p>

          {/* Issuer Company */}
          <div className="border-2 border-[#FF6B35] rounded-lg p-4 bg-[#FF6B35] bg-opacity-5" data-testid="rfq-issuer-company-section">
            <div className="flex items-center space-x-2 mb-3">
              <Building2 className="h-5 w-5 text-[#FF6B35]" />
              <label className="block text-sm font-bold text-[#2B2D42]">Issuer Company *</label>
            </div>
            <select required value={formData.company_id}
              onChange={(e) => { setFormData({ ...formData, company_id: e.target.value }); if (e.target.value) loadCompanySnapshot(e.target.value); }}
              className="w-full px-3 py-2 border-2 border-[#FF6B35] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35] font-medium"
              data-testid="rfq-company-select">
              <option value="">Select Issuer Company</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.trading_name || c.legal_name} ({c.country})</option>)}
            </select>
            {companySnapshot && (
              <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                <div className="text-xs space-y-1">
                  <div className="flex justify-between"><span className="text-[#6C757D]">Legal Name:</span><span className="font-medium">{companySnapshot.legal_name}</span></div>
                  <div className="flex justify-between"><span className="text-[#6C757D]">Country:</span><span className="font-medium">{companySnapshot.country}</span></div>
                  {companySnapshot.license_number && <div className="flex justify-between"><span className="text-[#6C757D]">License:</span><span className="font-medium">{companySnapshot.license_number}</span></div>}
                  {companySnapshot.trn && <div className="flex justify-between"><span className="text-[#6C757D]">TRN:</span><span className="font-medium">{companySnapshot.trn}</span></div>}
                  <div className="flex justify-between"><span className="text-[#6C757D]">Currency:</span><span className="font-bold text-[#FF6B35]">{formData.currency}</span></div>
                </div>
              </div>
            )}
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Supplier *</label>
              <div className="flex gap-2">
                <select required value={formData.supplier_id} onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                  className="flex-1 px-3 py-2 border rounded-lg" data-testid="rfq-supplier-select">
                  <option value="">Select Supplier</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.company_name || s.name}</option>)}
                </select>
                <Button type="button" size="sm" variant="outline" onClick={() => setShowNewSupplier(true)}
                  className="whitespace-nowrap border-[#00B4D8] text-[#00B4D8] hover:bg-[#00B4D8]/10"
                  data-testid="rfq-new-supplier-btn">
                  <Plus className="w-4 h-4 mr-1" /> New
                </Button>
              </div>
              {showNewSupplier && (
                <div className="mt-3 p-3 border border-[#00B4D8]/30 bg-[#00B4D8]/5 rounded-lg space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Company name *" value={newSupplier.company_name}
                      onChange={(e) => setNewSupplier({ ...newSupplier, company_name: e.target.value })}
                      className="px-2 py-1.5 border rounded text-sm" data-testid="new-supplier-name" />
                    <input placeholder="Email" value={newSupplier.email}
                      onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
                      className="px-2 py-1.5 border rounded text-sm" data-testid="new-supplier-email" />
                    <input placeholder="Phone" value={newSupplier.phone}
                      onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                      className="px-2 py-1.5 border rounded text-sm" />
                    <input placeholder="TRN / Tax ID" value={newSupplier.tax_id}
                      onChange={(e) => setNewSupplier({ ...newSupplier, tax_id: e.target.value })}
                      className="px-2 py-1.5 border rounded text-sm" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowNewSupplier(false)}>Cancel</Button>
                    <Button type="button" size="sm" onClick={saveNewSupplier} disabled={creatingSupplier || !newSupplier.company_name}
                      className="bg-[#00B4D8] hover:bg-[#0096c7]" data-testid="save-new-supplier">
                      {creatingSupplier ? 'Saving…' : 'Save & Select'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Currency</label>
              <select value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg" data-testid="rfq-currency-select">
                <option value="AED">AED (Default)</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Issue Date *</label>
              <input type="date" required value={formData.issue_date} onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Due Date *</label>
              <input type="date" required value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Project Name
                <span className="ml-2 text-xs font-normal text-[#6C757D]">— auto-fills <code className="bg-gray-100 px-1 rounded text-[11px]">{'{project_name}'}</code> in the letter body</span>
              </label>
              <input
                type="text"
                value={formData.project_name}
                onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                placeholder="e.g. FTA 9% Rollout Q2 2026"
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="rfq-project-name"
              />
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-[#2B2D42]">Requested Items</h4>
              <Button type="button" size="sm" onClick={addItem} className="bg-[#00B4D8] hover:bg-[#0096c7] text-white" data-testid="rfq-add-item-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            </div>
            {formData.items.map((item, index) => (
              <div key={index} className="border rounded-lg p-4 mb-3 space-y-3" data-testid={`rfq-item-${index}`}>
                <div className="flex justify-between items-start">
                  <span className="text-sm font-medium text-[#2B2D42]">Item {index + 1}</span>
                  {formData.items.length > 1 && (
                    <button type="button" onClick={() => removeItem(index)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#6C757D]">Product/Service Name *</label>
                    <ProductAutocomplete
                      value={item.product_name}
                      onChange={(v) => handleItemChange(index, 'product_name', v)}
                      onSelect={(p) => pickProduct(index, p)}
                      testId={`rfq-item-${index}-product`}
                      placeholder="Pesquise ou crie novo produto…"
                      inputClassName="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#6C757D]">Description</label>
                    <input value={item.description || ''} onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Description" />
                    <InternalDataWarning text={item.description} testId={`item-${index}-internal-warning`} />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs text-[#6C757D]">Quantity</label>
                    <input type="number" min="1" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-[#6C757D]">Unit</label>
                    <select value={item.unit} onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm">
                      <option value="units">Units</option><option value="kg">Kg</option><option value="liters">Liters</option>
                      <option value="hours">Hours</option><option value="days">Days</option><option value="boxes">Boxes</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[#6C757D]">Unit Price (optional)</label>
                    <input type="number" min="0" step="0.01" value={item.unit_price ?? ''} onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm" placeholder={isLoi ? 'Optional' : 'Supplier fills'} />
                  </div>
                  <div>
                    <label className="text-xs text-[#6C757D]">Tax Rate (%)</label>
                    <select value={item.tax_rate} onChange={(e) => handleItemChange(index, 'tax_rate', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm">
                      <option value="0">0%</option><option value="5">5% (UAE VAT)</option><option value="10">10%</option><option value="15">15%</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Notes & Terms */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[#2B2D42]">Terms</label>
                <div className="w-[260px]">
                  <PaymentTermPicker
                    currentValue={formData.terms}
                    onInsert={(v) => setFormData((p) => ({ ...p, terms: v }))}
                    testId="rfq-terms-payment-picker"
                  />
                </div>
              </div>
              <textarea value={formData.terms} onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm" rows={isLoi ? 6 : 3} />
              <InternalDataWarning text={formData.terms} testId="terms-internal-warning" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                {isLoi ? 'Letter Body (editable)' : 'Notes'}
              </label>
              <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono leading-relaxed"
                rows={isLoi ? 14 : 3}
                placeholder={isLoi ? 'Institutional letter body — placeholders like {supplier_name} are auto-filled.' : 'Optional notes visible to the supplier.'}
                data-testid="rfq-notes-textarea"
              />
              <InternalDataWarning text={formData.notes} testId="notes-internal-warning" />
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className={isLoi ? 'bg-[#023E8A] hover:bg-[#0353A4]' : 'bg-[#FF6B35] hover:bg-[#E5562D]'} data-testid="rfq-submit-btn">
              {loading
                ? (isEditMode ? 'Saving...' : 'Creating...')
                : `${isEditMode ? 'Save' : 'Create'} ${isLoi ? 'LOI' : 'RFQ'}`}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRFQModal;
